import React from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from 'react-flow-renderer';

function parseJsonToGraph(data) {
  const nodes = Object.entries(data).map(([id, info]) => ({
    id,
    data: { label: info.name || id },
    type: info.type,
    position: { x: 0, y: 0 },
    sourcePosition: 'bottom',
    targetPosition: 'left',
  }));
  const edges = [];
  Object.entries(data).forEach(([id, info]) => {
    if (info.goto) {
      edges.push({ id: `e-${id}-${info.goto}`, source: id, target: info.goto, type: 'smoothstep', markerEnd: { type: 'arrow' } });
    }
    if (info.cases) {
      info.cases.forEach((c) => {
        const [[condition, target]] = Object.entries(c);
        edges.push({ id: `e-${id}-${target}`, source: id, target, label: condition, type: 'smoothstep', markerEnd: { type: 'arrow' } });
      });
    }
  });

  return { nodes, edges };
}

function findCycleStartNodes(edges) {
  const adjList = {};
  const visited = new Set();
  const recStack = new Set();
  const cycleNodes = new Set();

  edges.forEach(({ source, target }) => {
    if (!adjList[source]) adjList[source] = [];
    adjList[source].push(target);
  });

  function dfs(node) {
    if (recStack.has(node)) {
      cycleNodes.add(node);
      return true;
    }
    if (visited.has(node)) return false;

    visited.add(node);
    recStack.add(node);

    if (adjList[node]) {
      for (const neighbor of adjList[node]) {
        if (dfs(neighbor)) cycleNodes.add(node);
      }
    }
    recStack.delete(node);
    return false;
  }

  Object.keys(adjList).forEach((node) => {
    if (!visited.has(node)) dfs(node);
  });

  return cycleNodes;
}

function organizeDag(nodes, edges) {
  const cycleStartNodes = findCycleStartNodes(edges);  
  const filteredEdges = edges.filter(({ target }) => !cycleStartNodes.has(target));
  const adjList = {};
  const inDegree = {};
  nodes.forEach((node) => {
    adjList[node.id] = [];
    inDegree[node.id] = 0;
  });
  filteredEdges.forEach((edge) => {
    adjList[edge.source].push(edge.target);
    inDegree[edge.target]++;
  });
  const queue = [];
  const layers = {};
  let maxLayer = 0;
  Object.keys(inDegree).forEach((node) => {
    if (inDegree[node] === 0) {
      queue.push(node);
      layers[node] = 0;
    }
  });
  while (queue.length > 0) {
    const current = queue.shift();
    const currentLayer = layers[current];
    adjList[current].forEach((neighbor) => {
      inDegree[neighbor]--;
      if (inDegree[neighbor] === 0) {
        queue.push(neighbor);
        layers[neighbor] = currentLayer + 1;
        maxLayer = Math.max(maxLayer, layers[neighbor]);
      }
    });
  }
  const grid = Array.from({ length: maxLayer + 1 }, () => []);
  Object.entries(layers).forEach(([node, layer]) => {
    grid[layer].push(node);
  });
  const positions = {};
  const paddingX = 250;
  const paddingY = 200;
  grid.forEach((layerNodes, layerIndex) => {
    layerNodes.sort().reverse();
    const totalWidth = (layerNodes.length - 1) * paddingX;
    const startX = totalWidth / 2;
    layerNodes.forEach((node, index) => {
      positions[node] = { x: startX - index * paddingX, y: layerIndex * paddingY };
    });
  });
  return nodes.map((node) => ({
    ...node,
    position: positions[node.id] || { x: 0, y: 0 },
  }));
}

const DagEditor = () => {
  const { nodes: initialNodes, edges: initialEdges } = parseJsonToGraph({
    "@User_ban": {
        "say": "${Restrição}",
        "goto": "@User_complement_ask",
        "name": "Bot Censor",
        "type": "#bot",
        "remains": [
            "Pergunta original"
        ]
    },
    "@Bot_answer": {
        "say": "Aqui estão seus resultados. Deseja algo mais?",
        "goto": "@User_initial_add",
        "name": "Bot Publicador",
        "type": "#bot",
        "publish": true
    },
    "@Agent_coder": {
        "goto": "@Tool_validator",
        "name": "Bot Codificador",
        "type": "#agent",
        "cache": "#overwrite()",
        "scheme": "generic-coder-0.0.0",
        "function": "dialoger"
    },
    "@Erro_formula": {
        "say": "Infelizmente não foi possível atender a sua solicitação nessa versão. Estamos trabalhando para aumentar os poderes dessa solução. <br />Por favor, tente perguntar o que você me pediu de uma forma diferente, sempre um pedido de cada vez.",
        "goto": "@User_initial",
        "name": "Bot Humilde",
        "type": "#bot",
        "remains": [
            "Pergunta original"
        ]
    },
    "@User_initial": {
        "ask": "${Pergunta original}",
        "goto": "@Agent_paraphraser",
        "name": "Você Perguntando",
        "type": "#user",
        "cache": "#overwrite()",
        "start": true
    },
    "@Agent_reporter": {
        "goto": "@Bot_answer",
        "name": "Bot Reportador",
        "send": [
            "Pergunta original",
            "Pergunta melhorada",
            "Racional de cálculo",
            "Fórmulas intermediárias",
            "Explicação das fórmulas intermediárias",
            "Fórmula principal",
            "Resultado",
            "Preliminares"
        ],
        "type": "#agent",
        "cache": "#overwrite()",
        "limit": 50.0,
        "logger": true,
        "scheme": "generic-rep-0.0.0",
        "function": "dialoger"
    },
    "@Tool_validator": {
        "url": "calculator/check",
        "body": {
            "name": "KZMIRO_POC",
            "clientId": "${clientId}",
            "calculation": {
                "Resultado": "${Código principal}"
            }
        },
        "name": "Checador de Fórmula",
        "type": "#tool",
        "cache": "#overwrite()",
        "cases": [
            {
                "!!${Erro}": "@Erro_formula"
            },
            {
                "$else": "@Tool_calculator"
            }
        ]
    },
    "@Tool_calculator": {
        "url": "calculator/eval",
        "body": {
            "name": "KZMIRO_POC",
            "clientId": "${clientId}",
            "ellipsis": {
                "Preliminares": 256.0
            },
            "calculation": {
                "Resultado": "${Código principal}",
                "Preliminares": "${Códigos intermediários}"
            }
        },
        "goto": "@Agent_reporter",
        "name": "Avaliador de Fórmula",
        "type": "#tool",
        "cache": "#overwrite()"
    },
    "@User_initial_add": {
        "ask": "${Pergunta original}",
        "goto": "@Agent_paraphraser",
        "name": "Você Perguntando de Novo",
        "type": "#user",
        "cache": "#renew()"
    },
    "@Agent_paraphraser": {
        "name": "Bot Parafraseador",
        "type": "#agent",
        "cache": "#overwrite()",
        "cases": [
            {
                "!!${Restrição}": "@User_ban"
            },
            {
                "!!${Dúvida}": "@User_complement_say"
            },
            {
                "$else": "@User_just_to_know"
            }
        ],
        "scheme": "generic-paraf-0.0.0",
        "function": "dialoger"
    },
    "@User_just_to_know": {
        "say": "Baseado em sua pergunta original: \"<b>${Pergunta original}</b>\", segue uma versão mais detalhada do que eu entendi! <br/> <br/> <b>${Pergunta melhorada}</b> <br/> <br/> Para chegar lá, vou seguir o seguinte racional de cálculo:",
        "goto": "@Agent_coder",
        "name": "Bot Confirmador",
        "type": "#bot"
    },
    "@User_complement_ask": {
        "ask": "${Complemento}",
        "goto": "@Agent_paraphraser",
        "name": "Você Complementando",
        "type": "#user",
        "cache": "#appendAll()"
    },
    "@User_complement_say": {
        "say": "Baseado em sua pergunta original: \"<b>${Pergunta original}</b>\", tenho alguns pontos para esclarecer. <br/> ${Dúvida}",
        "goto": "@User_complement_ask",
        "name": "Bot Analista",
        "type": "#bot"
    }
  });

  const organizedNodes = organizeDag(initialNodes, initialEdges);
  const [nodes, setNodes, onNodesChange] = useNodesState(organizedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={(params) => setEdges((eds) => addEdge(params, eds))}
          fitView
        >
          <Controls />
          <Background />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export default DagEditor;